import format from 'date-fns/format';

/**
 * Parses the JSON returned by a network request.
 * @param  {Object} response A response from a network request.
 * @return {Object}          The parsed JSON from the request.
 */
function parseJSON(response: Response) {
  return response.json();
}

/**
 * Parses the HTML returned by a network request.
 * @param  {Object} response A response from a network request.
 * @return {Object}          The parsed HTML from the request.
 */
function parseHTML(response: Response) {
  return response.text();
}

/**
 * Checks if a network request came back fine, and throws an error if not.
 * @param  {Object} response    A response from a network request.
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) return response;
  const error = new Error(response.statusText);
  (error as any).response = response;
  return response
    .json()
    .then(r => {
      (error as any).responseJson = r;
      throw error;
    })
    .catch(() => {
      throw error;
    });
}

/**
 * Generate authorization header data.
 * @param  {string} accessToken The access token to include in the header.
 * @return {string}             Authorization header value.
 */
const authHeader = (accessToken: string) => {
  const authObj = {
    access_token: accessToken,
    client_token: null,
    mpx_token: null,
    nonce: null,
    timestamp: format(new Date(), 'yyyyMMddTHH:mm:ssxx'),
    signature: null,
  };

  return Object.entries(authObj)
    .map(([key, value]) => `${key}=${value}`)
    .join(';');
};

/**
 * Private helper function for creating a request.
 * @param {string} method        HTTP method for the request.
 * @param {Object} [headers]     Object with header keys and values.
 * @param {string} [body]        Request body to send with the request.
 * @param {string} [accessToken] Access token for sending authorization header.
 * @param {string} [credentials] Send credentials in case of cross-origin requests.
 * @return {Object}              Options object to be used in the fetch request.
 */
const createRequest = (
  method: string,
  headers: HeadersInit,
  body?: BodyInit | null,
  accessToken?: string | null,
  credentials?: RequestCredentials | null
): RequestInit => {
  const headersWithOptionals = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: authHeader(accessToken) }),
    ...headers,
  };

  return {
    method,
    headers: headersWithOptionals,
    ...(credentials && { credentials }),
    ...(body && { body: JSON.stringify(body) }),
  };
};

/**
 * Creates and returns the payload for a POST request.
 * @param  {Object} body        The JSON body.
 * @param  {string} accessToken The accessToken to be used with auth.
 * @return {Object}             The payload to be used with fetch.
 */
export const createAuthPostRequest = (body: BodyInit, accessToken: string) =>
  createRequest('POST', {}, body, accessToken, null);

/**
 * Creates and returns the payload for a PUT request with auth.
 * @param  {string} accessToken The accessToken to be used with auth.
 * @return {Object}             The payload to be used with fetch.
 */
export const createAuthPutRequest = (accessToken: string) =>
  createRequest('PUT', {}, null, accessToken, null);

/**
 * Creates and returns the payload for a GET request with auth.
 * @param  {string} accessToken The accessToken to be used with auth.
 * @return {Object}             The payload to be used with fetch.
 */
export const createAuthGetRequest = (accessToken: string) =>
  createRequest('GET', {}, null, accessToken, null);

/**
 * Creates and returns the payload for a DELETE request with auth.
 * @param  {string} accessToken The accessToken to be used with auth.
 * @return {Object}             The payload to be used with fetch.
 */
export const createAuthDeleteRequest = (accessToken: string) =>
  createRequest('DELETE', {}, null, accessToken, null);

/**
 * Creates and returns the payload for a POST request.
 * @param  {Object} body The JSON body.
 * @return {Object}      The payload to be used with fetch.
 */
export const createPostRequest = (body: any, headers: HeadersInit = {}) =>
  createRequest('POST', headers, body, null, 'same-origin');

/**
 * Created and returns the payload for a POST request as a Promise.
 * @param {string} url The GraphQL endpoint url.
 * @param {object} body The JSON body.
 * @param {object} [headers]     Object with header keys and values.
 * @return {Promise}  Promise resolving with data or rejecting with error.
 */
export const createPostRequestPromise = <Result>(
  url: string,
  body: BodyInit,
  headers: HeadersInit = {}
): Promise<Result | undefined> =>
  requestJSON<Result>(url, createPostRequest(body, headers)).then(({ err, data }) =>
    err ? Promise.reject(new Error(err)) : data
  );

/**
 * Creates and returns the payload for a GET request
 * @return {Object} The payload to be used with fetch
 */
export const createGetRequest = () =>
  createRequest('GET', { Accept: 'text/html' }, null, null, 'same-origin');

/**
 * Utility function for requesting a GraphQL endpoint.
 * @param  {string} url            The GraphQL endpoint url.
 * @param  {string} query          GraphQL query.
 * @param  {Object} [variables={}] Optional object with query variables.
 * @param  {string} operationName  Name of the operation to be executed.
 * @return {Promise}               Promise resolving with data or rejecting with error.
 */
// eslint-disable-next-line default-param-last
export function createPostGraphqlRequest<T = any>(
  url: string,
  query: string,
  variables: { [key: string]: any } = {},
  operationName: string = '',
  headers: HeadersInit | undefined = undefined
): Promise<T> {
  return requestJSON<T>(url, createPostRequest({ query, variables, operationName }, headers))
    .then(({ err, data }: any) => (err ? Promise.reject(err) : data))
    .then(res => (res?.errors?.[0] ? Promise.reject(res) : res.data));
}

/**
 * Returns a promise resolving to the JSON response of the requested url.
 * @param  {string} url       The URL to request.
 * @param  {Object} [options] The options to pass to `fetch`.
 * @return {Promise<any>}           An object containing either `data` or `err`.
 */
export function requestJSON<Result>(
  url: string,
  options?: RequestInit
): Promise<{ data?: Result; err?: any }> {
  return fetch(url, options)
    .then(checkStatus)
    .then(parseJSON)
    .then(data => ({ data } as { data: Result }))
    .catch(err => ({ err }));
}

/**
 * Returns a promise resolving to the HTML response of the requested url.
 * @param  {string} url       The URL to request.
 * @param  {Object} [options] The options to pass to `fetch`.
 * @return {Object}           An object containing either `data` or `err`.
 */
export function requestHTML(url: string, options: RequestInit) {
  return fetch(url, options)
    .then(checkStatus)
    .then(parseHTML)
    .then(data => ({ data }))
    .catch(err => ({ err }));
}

/**
 * Returns a promise resolving to the raw response of the requested url.
 * @param  {string} url       The URL to request.
 * @param  {Object} [options] The options to pass to `fetch`.
 * @return {Object}           An object containing either `data` or `err`.
 */
export function request(url: string, options: RequestInit) {
  return fetch(url, options)
    .then(checkStatus)
    .then(data => ({ data }))
    .catch(err => ({ err }));
}
